import { SharedEvents, type AppointmentIdCopiedPayload } from '@satellite/analytics/sharedEvents';

export type { AppointmentIdCopiedPayload };

/**
 * Human-readable event names sent to Mixpanel.
 *
 * Event strings are kept byte-identical to the retired legacy `enums/MixpanelEvents.js`
 * entries so Mixpanel history, cohorts and saved reports keep working. Property names in
 * the payload interfaces below are preserved for the same reason, which is why three
 * casing conventions coexist here (`'Warehouse ID'`, `appointmentId`, `carrier_company_id`).
 * Standardising them is a product decision tracked separately.
 */
export const Events = {
  AUTH: {
    SIGN_IN_PAGE_VIEWED: 'Viewed: Sign-in Page',
    LOGGED_IN: 'Action: Logged In',
    REGISTRATION_PAGE_VIEWED: 'Viewed: Registration Page',
    ACCOUNT_CREATED: 'Account: Created',
    ACTIVATION_EMAIL_VIEWED: 'Viewed: Activation Email Sent',
    ACCOUNT_ACTIVATED: 'Action: Activate Account',
    ACCOUNT_VERIFIED: 'Account: Verified'
  },
  COMPANY: {
    CREATED: 'Created: Carrier Company',
    UPDATED: 'Updated: Carrier Company'
  },
  WAREHOUSE: {
    SEARCHED: 'Action: Search Warehouse',
    SEARCH_ORDER_CHANGED: 'Action: Change Warehouse Search Order',
    SELECTED: 'Action: Select Warehouse',
    DETAILS_VIEWED: 'Viewed: Warehouse Details Page',
    ADDED_TO_FAVORITES: 'Action: Add Warehouse to Favorites',
    BOOK_CLICKED: 'Action: Click Button to Book Warehouse'
  },
  APPOINTMENT: {
    BOOKING_PAGE_VIEWED: 'Viewed: Book Appointment Homepage',
    LOAD_TYPE_SELECTED: 'Action: Load Type Selected',
    DOCK_SELECTED: 'Action: Dock Selected',
    DATE_TIME_SELECTED: 'Action: Date & Time Selected',
    BOOK_CLICKED: 'Action: Book Appointment Button Clicked',
    CREATED: 'Action: Appointment Created',
    LIST_VIEWED: 'Viewed: My Appointments Page',
    SEARCHED: 'Action: Search Appointments',
    FILTERED_BY_STATUS: 'Action: Filter Appointments by Status',
    FILTERED_BY_DATE: 'Action: Filter Appointments by Date',
    EXPORTED: 'Action: Export Appointments',
    DETAILS_VIEWED: 'Viewed: Appointment Details',
    EDITED: 'Action: Edit Appointment Details',
    RESCHEDULED: 'Action: Reschedule Appointment',
    CANCELED: 'Action: Cancel Appointment',
    ETA_PROVIDED: 'Action: Provide ETA',
    PRINTED: 'Action: Print Appointment',
    // Fired from the shared appointmentMixinBase (libs/satellite), also consumed by Helios.
    ID_COPIED: SharedEvents.APPOINTMENT_ID_COPIED
  },
  LANGUAGE: {
    SWITCHED: 'Switched: Application Language'
  },
  // Post-booking role survey experiment.
  ROLE_SURVEY: {
    SHOWN: 'Viewed: Role Survey',
    SUBMITTED: 'Action: Submit Role Survey',
    DISMISSED: 'Action: Dismiss Role Survey'
  }
} as const;

/** Where in the app the carrier reached the company step. */
export type CompanyEntryPoint = 'registration flow' | 'change company';

/** Payload type for "Created: Carrier Company". */
export interface CarrierCompanyCreatedPayload {
  entry_point: CompanyEntryPoint;
  carrier_company_id: string;
  carrier_company_name: string;
  carrier_company_usdot?: string | null;
}

/**
 * Payload type for "Updated: Carrier Company".
 *
 * `search_by` is only populated by CompanySearch.vue; CreateCompanyDialog.vue attaches an
 * already-known company and has no search term to report, hence the optional field.
 */
export interface CarrierCompanyUpdatedPayload extends CarrierCompanyCreatedPayload {
  search_by?: 'usdot number' | 'company name';
}

/**
 * Warehouse identity as sent by the warehouse browsing events. Luna does not register
 * warehouse as a Mixpanel super property, so every event repeats it in the payload.
 */
export interface WarehouseRefPayload {
  'Warehouse Name': string;
  'Warehouse ID': string;
}

/** Payload type for "Action: Search Warehouse". */
export interface WarehouseSearchedPayload {
  'Search String': string;
  'Number of Results': number;
}

/** Payload type for "Action: Change Warehouse Search Order". */
export interface WarehouseSearchOrderChangedPayload {
  'Sort By': string | null;
}

/** Payload type for "Action: Select Warehouse". */
export type WarehouseSelectedPayload = WarehouseRefPayload;

/** Payload type for "Viewed: Warehouse Details Page". */
export type WarehouseDetailsViewedPayload = WarehouseRefPayload;

/** Payload type for "Action: Add Warehouse to Favorites". Removing a favorite is not tracked. */
export type WarehouseAddedToFavoritesPayload = WarehouseRefPayload;

/** Payload type for "Action: Click Button to Book Warehouse". */
export type WarehouseBookClickedPayload = WarehouseRefPayload;

/**
 * Warehouse identity as sent by the booking flow events. Same two properties as
 * WarehouseRefPayload but declared separately because the booking page falls back to
 * the literal 'Unknown' when the warehouse has not loaded yet.
 */
export interface BookingWarehouseRefPayload {
  'Warehouse ID': string;
  'Warehouse Name': string;
}

/** Payload type for "Action: Book Appointment Button Clicked". Fires on attempt, before the API responds. */
export type AppointmentBookClickedPayload = BookingWarehouseRefPayload;

/** Payload type for "Action: Appointment Created". Fires only after the API confirms the booking. */
export type AppointmentCreatedPayload = BookingWarehouseRefPayload;

/** Payload type for "Action: Load Type Selected". */
export interface AppointmentLoadTypeSelectedPayload extends BookingWarehouseRefPayload {
  'Loadtype Name': string;
  'Loadtype ID': string;
}

/** Payload type for "Action: Dock Selected". */
export interface AppointmentDockSelectedPayload extends BookingWarehouseRefPayload {
  'Dock Name': string;
  'Dock ID': string;
}

/** Payload type for "Action: Date & Time Selected". `Selected Time` is an ISO 8601 string. */
export interface AppointmentDateTimeSelectedPayload extends BookingWarehouseRefPayload {
  'Selected Time': string | null;
}

/** Payload type for "Action: Search Appointments". */
export interface AppointmentsSearchedPayload {
  'Search String': string;
}

/** Payload type for "Action: Filter Appointments by Status". Sends 'None' when the filter is cleared. */
export interface AppointmentsFilteredByStatusPayload {
  'Status Selected': string;
}

/** Payload type for "Action: Filter Appointments by Date". */
export interface AppointmentsFilteredByDatePayload {
  'Date Range Selected': string;
}

/** Payload type for "Viewed: Appointment Details". `Public` marks the no-login shared link. */
export interface AppointmentDetailsViewedPayload {
  'Warehouse ID'?: string;
  'Warehouse Name'?: string;
  appointmentId: string;
  Public: 'Yes' | 'No';
}

/**
 * Shared payload for the appointment management actions (edit, reschedule, cancel, ETA).
 * All four fire on attempt, before the API responds.
 */
export interface AppointmentActionPayload {
  appointmentId: string;
  'Warehouse ID': string;
  'Warehouse Name': string;
}

/** Payload type for "Action: Edit Appointment Details". */
export type AppointmentEditedPayload = AppointmentActionPayload;

/** Payload type for "Action: Reschedule Appointment". */
export type AppointmentRescheduledPayload = AppointmentActionPayload;

/** Payload type for "Action: Cancel Appointment". */
export type AppointmentCanceledPayload = AppointmentActionPayload;

/** Payload type for "Action: Provide ETA". */
export type AppointmentEtaProvidedPayload = AppointmentActionPayload;

/** Payload type for "Action: Print Appointment". */
export interface AppointmentPrintedPayload {
  appointmentId: string;
}

/**
 * Payload type for "Switched: Application Language".
 * `application_language` is the human-readable label shown in the site language
 * selector (e.g. `'Español (MX)'`), matching the Mixpanel super property of the same name.
 */
export interface ApplicationLanguageSwitchedPayload {
  application_language: string;
}

/**
 * Shared context on every role survey event. `user_id` is sent explicitly because Luna's
 * Mixpanel distinct_id is the user's email (see useLunaAnalytics); reports dedupe by it.
 * `region` is the company's ISO 3166-1 alpha-3 country code.
 */
export interface RoleSurveyContextPayload {
  user_id: string;
  company_id: string;
  appointment_id: string;
  region: string;
}

/** Payload type for "Viewed: Role Survey". */
export type RoleSurveyShownPayload = RoleSurveyContextPayload;

/** Payload type for "Action: Submit Role Survey". `other_text` is only sent when role is `other`. */
export interface RoleSurveySubmittedPayload extends RoleSurveyContextPayload {
  role: string;
  booking_for_other_company: boolean;
  other_text?: string;
}

/**
 * Payload type for "Action: Dismiss Role Survey" (Dismiss button or the X). `step` is the question
 * on screen when the user left (1 = role, 2 = other company's freight).
 */
export interface RoleSurveyDismissedPayload extends RoleSurveyContextPayload {
  step: 1 | 2;
}

/** Map event keys to payload types. */
export type LunaEventsMap = {
  [Events.AUTH.SIGN_IN_PAGE_VIEWED]: void;
  [Events.AUTH.LOGGED_IN]: void;
  [Events.AUTH.REGISTRATION_PAGE_VIEWED]: void;
  [Events.AUTH.ACCOUNT_CREATED]: void;
  [Events.AUTH.ACTIVATION_EMAIL_VIEWED]: void;
  [Events.AUTH.ACCOUNT_ACTIVATED]: void;
  [Events.AUTH.ACCOUNT_VERIFIED]: void;
  [Events.COMPANY.CREATED]: CarrierCompanyCreatedPayload;
  [Events.COMPANY.UPDATED]: CarrierCompanyUpdatedPayload;
  [Events.WAREHOUSE.SEARCHED]: WarehouseSearchedPayload;
  [Events.WAREHOUSE.SEARCH_ORDER_CHANGED]: WarehouseSearchOrderChangedPayload;
  [Events.WAREHOUSE.SELECTED]: WarehouseSelectedPayload;
  [Events.WAREHOUSE.DETAILS_VIEWED]: WarehouseDetailsViewedPayload;
  [Events.WAREHOUSE.ADDED_TO_FAVORITES]: WarehouseAddedToFavoritesPayload;
  [Events.WAREHOUSE.BOOK_CLICKED]: WarehouseBookClickedPayload;
  [Events.APPOINTMENT.BOOKING_PAGE_VIEWED]: void;
  [Events.APPOINTMENT.LOAD_TYPE_SELECTED]: AppointmentLoadTypeSelectedPayload;
  [Events.APPOINTMENT.DOCK_SELECTED]: AppointmentDockSelectedPayload;
  [Events.APPOINTMENT.DATE_TIME_SELECTED]: AppointmentDateTimeSelectedPayload;
  [Events.APPOINTMENT.BOOK_CLICKED]: AppointmentBookClickedPayload;
  [Events.APPOINTMENT.CREATED]: AppointmentCreatedPayload;
  [Events.APPOINTMENT.LIST_VIEWED]: void;
  [Events.APPOINTMENT.SEARCHED]: AppointmentsSearchedPayload;
  [Events.APPOINTMENT.FILTERED_BY_STATUS]: AppointmentsFilteredByStatusPayload;
  [Events.APPOINTMENT.FILTERED_BY_DATE]: AppointmentsFilteredByDatePayload;
  [Events.APPOINTMENT.EXPORTED]: void;
  [Events.APPOINTMENT.DETAILS_VIEWED]: AppointmentDetailsViewedPayload;
  [Events.APPOINTMENT.EDITED]: AppointmentEditedPayload;
  [Events.APPOINTMENT.RESCHEDULED]: AppointmentRescheduledPayload;
  [Events.APPOINTMENT.CANCELED]: AppointmentCanceledPayload;
  [Events.APPOINTMENT.ETA_PROVIDED]: AppointmentEtaProvidedPayload;
  [Events.APPOINTMENT.PRINTED]: AppointmentPrintedPayload;
  [Events.APPOINTMENT.ID_COPIED]: AppointmentIdCopiedPayload;
  [Events.LANGUAGE.SWITCHED]: ApplicationLanguageSwitchedPayload;
  [Events.ROLE_SURVEY.SHOWN]: RoleSurveyShownPayload;
  [Events.ROLE_SURVEY.SUBMITTED]: RoleSurveySubmittedPayload;
  [Events.ROLE_SURVEY.DISMISSED]: RoleSurveyDismissedPayload;
};
